import { useState, useEffect } from "react";
import { CloseButton, FormButton } from "reusableComponents";
import { useDispatch, useSelector } from "react-redux";
import { updateFilters } from "store/filters/operations";
import { selectFilters } from "store/filters/selectors";
import { TypeSelector, UsagesFilter } from "./components";
import { CapacityFilter } from "./components";
import { PriceTypeFilterButton } from "./components";
import { PriceOrderFilterButtonMobile } from "./components";
import { StylesFilter } from "./components";
import { useTranslation } from "react-i18next";
import getCapacityOptions from "helpers/getCapacityOptions";

import styles from "./styles.module.scss";

const FiltersFormMobile = ({ closeForm }) => {
  const dispatch = useDispatch();
  const [t] = useTranslation("global");
  const filters = useSelector(selectFilters);
  
  // Log initial filters from Redux
  useEffect(() => {
    console.log("📱 FiltersFormMobile - Initial Redux filters:", filters);
    console.log("📱 FiltersFormMobile - Initial type from Redux:", filters.type);
  }, []);

  // Local state for storing filter values before final submission
  const [localFilters, setLocalFilters] = useState({
    capacity: filters.capacity || null,
    usages: filters.usages || [],
    price_type: filters.price_type || null,
    min_price: filters.min_price || null,
    max_price: filters.max_price || null,
    type: filters.type || null,
    style: filters.style || null,
    order: filters.order || null,
    order_type: filters.order_type || null,
    order_price_type: filters.order_price_type || null,
  });

  // Log when localFilters changes
  useEffect(() => {
    console.log("📱 FiltersFormMobile - localFilters updated:", {
      type: localFilters.type,
      allFilters: localFilters
    });
  }, [localFilters]);

  // Log when Redux filters change
  useEffect(() => {
    console.log("📱 FiltersFormMobile - Redux filters changed:", {
      type: filters.type,
      allFilters: filters
    });
  }, [filters]);

  const capacityOptions = getCapacityOptions(localFilters, t);

  const handleCapacityChange = (updatedCapacity) => {
    console.log("📱 Capacity changed to:", updatedCapacity);
    setLocalFilters((prev) => ({ ...prev, capacity: updatedCapacity }));
  };

  const handleUsageSelect = (usages) => {
    console.log("📱 Usages changed to:", usages);
    setLocalFilters((prev) => ({
      ...prev,
      usages,
      capacity: null,
      price_type: null,
      min_price: null,
      max_price: null,
      type: null,
      style: null,
    }));
  };

  const handlePriceSelect = (priceType, minPrice, maxPrice) => {
    console.log("📱 Price changed to:", { priceType, minPrice, maxPrice });
    setLocalFilters((prev) => ({
      ...prev,
      price_type: priceType,
      min_price: minPrice,
      max_price: maxPrice,
    }));
  };

  const handleTypeChange = (newType) => {
    console.log("📱 FiltersFormMobile - handleTypeChange received:", newType);
    console.log("📱 FiltersFormMobile - current localFilters before update:", localFilters);
    
    setLocalFilters((prev) => {
      const updated = { ...prev, type: newType };
      console.log("📱 FiltersFormMobile - updated localFilters:", updated);
      return updated;
    });
  };

  const handleStyleChange = (newStyle) => {
    console.log("📱 Style changed to:", newStyle);
    setLocalFilters((prev) => ({ 
      ...prev, 
      style: newStyle 
    }));
  };

  const handleOrderChange = (orderUpdates) => {
    console.log("📱 Order changed to:", orderUpdates);
    setLocalFilters((prev) => ({ 
      ...prev,
      ...orderUpdates 
    }));
  };

  // Dispatch filters on final "Apply Filters" click
  const applyFilters = () => {
    console.log("📱 FiltersFormMobile - APPLYING FILTERS - localFilters:", localFilters);
    console.log("📱 FiltersFormMobile - type being applied:", localFilters.type);
    console.log("📱 FiltersFormMobile - current Redux filters before dispatch:", filters);
    
    // Dispatch the filters to Redux
    dispatch(updateFilters(localFilters));
    
    // Log after dispatch (note: this will show before Redux actually updates due to async)
    console.log("📱 FiltersFormMobile - dispatched updateFilters with:", localFilters);
    
    window.scrollTo({
      top: 0,
      behavior: "smooth",
    });
    
    setTimeout(() => {
      console.log("📱 FiltersFormMobile - closing form");
      closeForm();
    }, 500);
  };

  // Reset form values to initial state
  const resetFilters = () => {
    console.log("📱 FiltersFormMobile - resetting filters");
    const resetState = {
      capacity: null,
      usages: [],
      price_type: null,
      min_price: null,
      max_price: null,
      type: null,
      style: null,
      order: null,
      order_type: null,
      order_price_type: null,
    };
    console.log("📱 FiltersFormMobile - reset to:", resetState);
    setLocalFilters(resetState);
  };

  return (
    <div className={`${styles.filtersFormMobile} pageContainer`}>
      <div className={styles.header}>
        <CloseButton closeCallback={closeForm} />
      </div>
      
      <UsagesFilter
        onUsageChange={handleUsageSelect}
        selectedUsages={localFilters.usages}
      />
      
      <CapacityFilter
        capacityOptions={capacityOptions}
        filters={filters}
        selectedCapacity={localFilters.capacity}
        onCapacityChange={handleCapacityChange}
      />

      <PriceTypeFilterButton
        onPriceSelect={handlePriceSelect}
        selectedPriceType={localFilters.price_type}
        minPrice={localFilters.min_price}
        maxPrice={localFilters.max_price}
        selectedUsage={
          localFilters.usages.length > 0 ? localFilters.usages[0] : null
        }
      />
      
      <TypeSelector
        selectedType={localFilters.type}
        onTypeChange={handleTypeChange}
      />
      
      <StylesFilter 
        selectedStyle={localFilters.style}
        onStyleChange={handleStyleChange}
      />
      
      <PriceOrderFilterButtonMobile
        selectedOrder={localFilters.order}
        selectedOrderType={localFilters.order_type}
        selectedOrderPriceType={localFilters.order_price_type}
        onOrderChange={handleOrderChange}
      />
      
      <div className={styles.actions}>
        <FormButton
          label={t("search.filters.default")}
          onClick={resetFilters}
          variant="reset"
        />
        <FormButton label={t("buttons.confirm")} onClick={applyFilters} />
      </div>
    </div>
  );
};

export default FiltersFormMobile;